<template>
  <wizard-base class="rent-status" :title="title">
    <div class="rent-status__fields">
      <HdRadioCardGroup v-model="rentStatus" name="rentStatus" :mode="radioCardMode">
        <HdRadioCard
          v-for="status in rentStatuses"
          :key="status.value"
          :native-value="status.value"
        >
          <template #icon>
            <img :src="radioCardMode === 'card' ? status.icon : status.mobileIcon" />
          </template>
          {{ status.text }}
        </HdRadioCard>
      </HdRadioCardGroup>
      <PriceContributionInput
        v-model="availabilityPrice"
        name="availability"
        class="rent-status__input"
      />
    </div>
    <template v-if="shouldShowQuestionnaire" #secondary-section>
      <div class="rent-status__question">
        <h3 class="rent-status__question__title">
          {{ $t('WIZARD.GENERAL_QUESTIONS.QUESTIONS.COMMERCIAL_AREA') }}
        </h3>
        <TileSelectWithReset
          v-model="commercialArea"
          :items="commercialAreaOptions"
          name="hasCommercialArea"
          type="string"
          class="rent-status__question__options"
        />
      </div>
      <div v-if="shouldShowCommercialAreaReductionQuestion" class="rent-status__question">
        <h3 class="rent-status__question__title">
          {{ $t('WIZARD.GENERAL_QUESTIONS.QUESTIONS.COMMERCIAL_AREA_REDUCTION') }}
        </h3>
        <TileSelectWithReset
          v-model="commercialAreaReduction"
          :items="yesNoOptions"
          name="hasCommercialAreaReduction"
          type="string"
          class="rent-status__question__options"
        />
      </div>
    </template>
  </wizard-base>
</template>

<script>
// Components
import WizardBase from '@/layout/WizardBase.vue';
import PriceContributionInput from '@/components/PriceContributionInput.vue';
import { HdRadioCard, HdRadioCardGroup, OnResizeService } from 'homeday-blocks';
import TileSelectWithReset from '@/components/TileSelectWithReset.vue';
// Services
import { mapFeatures } from '@/services/feature';
import { mapPriceContributions } from '@/services/priceContribution';
import { isMobile } from '@/services/breakpoints';
// Constants
import { MUTATION } from '@/store/CONSTANTS';
import CONFIG from '@/config/env';

export default {
  name: 'RentStatusBaseSlide',
  components: {
    WizardBase,
    HdRadioCard,
    HdRadioCardGroup,
    PriceContributionInput,
    TileSelectWithReset,
  },
  inject: ['slideTransitionDuration'],
  props: {
    title: {
      type: String,
      required: true,
    },
    rentStatuses: {
      type: Array,
      required: true,
      validator(values) {
        const KEYS = ['text', 'value', 'icon'];
        const hasValues = values.length > 0;
        const hasKeys = values.every((value) => {
          const keys = Object.keys(value);
          const missingKeys = KEYS.filter((key) => !keys.includes(key));

          return missingKeys.length === 0;
        });

        return hasValues && hasKeys;
      },
    },
  },
  data() {
    return {
      commercialAreaOptions: [
        {
          text: this.$t('WIZARD.GENERAL_QUESTIONS.OPTION.NO_COMMERCIAL_SPACE'),
          value: 'no_commercial_area',
        },
        {
          text: this.$t('WIZARD.GENERAL_QUESTIONS.OPTION.LESS_THAN_25_PERCENT'),
          value: 'less_than25_percent',
        },
        {
          text: this.$t('WIZARD.GENERAL_QUESTIONS.OPTION.MORE_OR_EQUAL_THAN_25_PERCENT'),
          value: 'more_than25_percent',
        },
        {
          text: this.$t('WIZARD.GENERAL_QUESTIONS.OPTION.DONT_KNOW'),
          value: 'dont_know', // will be converted to null before sending the evaluation
        },
      ],
      yesNoOptions: [
        {
          text: this.$t('WIZARD.GENERAL_QUESTIONS.OPTION.YES'),
          value: true,
        },
        {
          text: this.$t('WIZARD.GENERAL_QUESTIONS.OPTION.NO'),
          value: false,
        },
        {
          text: this.$t('WIZARD.GENERAL_QUESTIONS.OPTION.DONT_KNOW'),
          value: 'dont_know', // will be converted to null before sending the evaluation
        },
      ],
      radioCardMode: isMobile() ? 'tile' : 'card',
    };
  },
  computed: {
    ...mapFeatures(['rentStatus', 'commercialArea', 'commercialAreaReduction']),
    ...mapPriceContributions(['availability']),
    shouldShowQuestionnaire() {
      return CONFIG.feature_flags.edm_questionnaire;
    },
    shouldShowCommercialAreaReductionQuestion() {
      return this.commercialArea === 'more_than25_percent';
    },
  },
  watch: {
    shouldShowCommercialAreaReductionQuestion(shouldShow) {
      if (!shouldShow) {
        this.$store.commit(MUTATION.WIZARD.CURRENT_DEAL.SET_FEATURE_VALUE, {
          name: 'commercialAreaReduction',
          value: undefined,
        });
      }
    },
  },
  created() {
    OnResizeService.onThrottled(this.updateRadioCardMode);
  },
  destroyed() {
    OnResizeService.offThrottled(this.updateRadioCardMode);
  },
  methods: {
    updateRadioCardMode() {
      if (isMobile()) {
        this.radioCardMode = 'tile';
      } else {
        this.radioCardMode = 'card';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.rent-status {
  overflow-x: hidden;

  ::v-deep .wizard-base__container {
    margin-right: 0;
    margin-left: 0;
  }

  &__input {
    width: 200px;
    margin: $sp-xl auto $sp-l;
  }

  ::v-deep .card img {
    width: 85px;
    height: 85px;
  }

  ::v-deep .card--tile img {
    width: 44px;
    height: 44px;
  }

  &__question {
    margin: 0 auto $sp-m;
    text-align: center;
    max-width: 30rem;
    h3 {
      margin-bottom: $sp-m;
    }
  }
}
</style>
